"use client";

/**
 * solicitudesSearch.ts
 * ────────────────────────────────────────────────────────────────
 * El listener en tiempo real de `useSolicitudes()` está limitado a las
 * ~300 solicitudes más recientes (ver `solicitudesStore.ts`) para no pagar
 * el coste de un listener sin límite. Eso significa que un cliente antiguo
 * podía quedar invisible al buscarlo en Archivo o en el buscador global.
 *
 * Esta función hace una lectura PUNTUAL (getDocs, no realtime) de toda la
 * colección, pensada para dispararse solo cuando el usuario busca
 * activamente — no se mantiene abierta, así que no añade coste continuo.
 * Se cachea en memoria un minuto para no repetir la consulta en cada
 * pulsación de tecla dentro de la misma sesión de búsqueda.
 * ────────────────────────────────────────────────────────────────
 */

import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { SolicitudPolizaV3 } from "@/types/solicitud";

const CACHE_TTL_MS = 60_000;

let cache: { data: SolicitudPolizaV3[]; fetchedAt: number } | null = null;
let inflight: Promise<SolicitudPolizaV3[]> | null = null;

export async function fetchAllSolicitudesOnce(): Promise<SolicitudPolizaV3[]> {
  const now = Date.now();
  if (cache && now - cache.fetchedAt < CACHE_TTL_MS) return cache.data;
  if (inflight) return inflight;

  inflight = (async () => {
    const q = query(collection(db, "solicitudes_polizas"), orderBy("fechaCreacion", "desc"));
    const snap = await getDocs(q);
    const data: SolicitudPolizaV3[] = [];
    snap.forEach((doc) => data.push({ ...doc.data(), id: doc.id } as SolicitudPolizaV3));
    cache = { data, fetchedAt: Date.now() };
    return data;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}
